'use client'

import { Check } from 'lucide-react'
import { PASSWORD_REQUIREMENT_LIST, getMetRequirements, calculatePasswordStrength } from '@/lib/password'

interface PasswordRequirementsProps {
  password: string
  showLabel?: boolean
}

export function PasswordRequirements({ password, showLabel = true }: PasswordRequirementsProps) {
  const metRequirements = getMetRequirements(password)
  const strength = calculatePasswordStrength(password)
  const isValid = metRequirements.length === PASSWORD_REQUIREMENT_LIST.length

  // Determine strength color
  let strengthColor = 'bg-slate-200'
  let strengthLabel = ''
  if (strength === 0) {
    strengthColor = 'bg-slate-200'
    strengthLabel = ''
  } else if (strength < 40) {
    strengthColor = 'bg-red-500'
    strengthLabel = 'Weak'
  } else if (strength < 80) {
    strengthColor = 'bg-teal-500'
    strengthLabel = 'Good'
  } else if (strength < 100) {
    strengthColor = 'bg-blue-500'
    strengthLabel = 'Strong'
  } else {
    strengthColor = 'bg-emerald-500'
    strengthLabel = 'Very Strong'
  }

  return (
    <div className="mt-3 space-y-3">
      {showLabel && (
        <div className="space-y-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Password Strength
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${strengthColor}`}
                style={{ width: `${strength}%` }}
              />
            </div>
            {strengthLabel && <span className="text-xs font-semibold text-slate-600">{strengthLabel}</span>}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Requirements
        </label>
        <div className="space-y-1.5">
          {PASSWORD_REQUIREMENT_LIST.map((requirement) => {
            const isMet = metRequirements.includes(requirement.id)
            return (
              <div
                key={requirement.id}
                className="flex items-center gap-2 rounded-lg px-3 py-2 bg-slate-50 transition-colors"
              >
                <div
                  className={`flex-shrink-0 rounded-full p-0.5 transition-colors ${
                    isMet ? 'bg-emerald-100' : 'bg-slate-200'
                  }`}
                >
                  <Check
                    className={`h-3 w-3 transition-colors ${
                      isMet ? 'text-emerald-600' : 'text-slate-300'
                    }`}
                  />
                </div>
                <span
                  className={`text-xs font-medium transition-colors ${
                    isMet ? 'text-emerald-700' : 'text-slate-500'
                  }`}
                >
                  {requirement.label}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {password && !isValid && (
        <div className="rounded-lg bg-teal-50 border border-teal-200 p-3">
          <p className="text-xs text-teal-800 font-medium">
            Password must meet all requirements above
          </p>
        </div>
      )}
    </div>
  )
}
